import { useRef, useState, useEffect } from 'react';

export default function HistogramChart({ data, title }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(400);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  if (!data || data.length === 0) return null;

  const maxCount = Math.max(...data.map(d => d.count));
  const pad = { top: 20, right: 10, bottom: 35, left: 40 };
  const h = 200;
  const chartW = width - pad.left - pad.right;
  const chartH = h - pad.top - pad.bottom;
  const barW = Math.max(2, chartW / data.length - 1);

  return (
    <div className="histogram-chart" ref={containerRef}>
      {title && <h4 className="chart-title">{title}</h4>}
      <svg width="100%" viewBox={`0 0 ${width} ${h}`} style={{ display: 'block' }}>
        {/* Y grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((r, i) => {
          const y = pad.top + chartH * (1 - r);
          return (
            <g key={i}>
              <line x1={pad.left} x2={width - pad.right} y1={y} y2={y}
                stroke="#1a1d2e" strokeWidth={0.5} />
              <text x={pad.left - 4} y={y + 4} textAnchor="end"
                fill="#555" fontSize={9}>{Math.round(maxCount * r)}</text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const barH = maxCount > 0 ? (d.count / maxCount) * chartH : 0;
          const x = pad.left + i * (chartW / data.length);
          const y = pad.top + chartH - barH;
          const mid = (d.bucket_min + d.bucket_max) / 2;
          const isPositive = mid >= 0;

          return (
            <g key={i}>
              <rect
                x={x} y={y} width={barW} height={barH}
                rx={1}
                fill={isPositive ? 'rgba(34, 197, 94, 0.7)' : 'rgba(239, 68, 68, 0.7)'}
              />
              {i % Math.max(1, Math.floor(data.length / 6)) === 0 && (
                <text x={x + barW / 2} y={h - 5} textAnchor="middle"
                  fill="#555" fontSize={8}
                  transform={`rotate(-20, ${x + barW / 2}, ${h - 5})`}>
                  {mid.toFixed(1)}
                </text>
              )}
            </g>
          );
        })}

        {/* Zero line label */}
        <text x={pad.left - 4} y={h - 18} textAnchor="end" fill="#8b8fa3" fontSize={8}>P&L</text>
      </svg>
    </div>
  );
}
